import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {SeasonPopupProps} from './season-popup.types';
import styles from './season-popup.module.scss';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Popover} from '@Components/popover';

export function SeasonPopup({isPositionFixed = false, doCloseOnOuterClick = true, ...props}: SeasonPopupProps): ReactElement<any> | null {
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = props.referenceElement.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {threshold: 0}
    );

    observer.observe(el);
    return (): void => {
      observer.disconnect();
    };
  }, [props.referenceElement]);

  const onOutsideClick = (): void => {
    if (!doCloseOnOuterClick) {
      return;
    }

    if (!isHidden) {
      setIsHidden(true);
    }
    props.onClose?.();
  };

  const handleCloseClick = (): void => {
    setIsHidden(true);

    props.onClose?.();
  };

  if (!isInView || isHidden) {
    return null;
  }

  return (
    <Popover
      referenceElementRef={props.referenceElement}
      popoverCloseFunction={onOutsideClick}
      alignment={props.arrowAlignment}
      hasArrow
      isPositionFixed={isPositionFixed}
      className={'bg-neutral-0 radius-8 border-s-standard shadow-static-elevation-3'}
      children={
        <div className={`flex-v-row  ${styles.popupDimensions} `}>
          <Icon
            onClick={handleCloseClick}
            icon="icon-close"
            className={`${styles.crossButtonPlacement} content-body _position-absolute`}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            ariaLabel={window.i18next.t('pmjws_close')}
            isUsedAsButton
          />
          <div className={`spacing-p-4 ${styles.popupGap}`}>
            <div className={`radius-4 _overflow-hidden ${styles.seasonImageStyles}`}>
              <img className={'_full-width '} src={props.imageUrl} alt={props.title} />
            </div>
            <Text size={TextSize.XSMALL} val={props.title} />
          </div>
        </div>
      }
    />
  );
}
